import type { AttentionItem } from '../dashboardShared/types';
import { shopifyMappingHref } from '../integrationDetailTabs';

export interface DeriveStoreAttentionInput {
  blockedCount: number;
  liveCount: number;
  totalSelected: number;
  /** Selected products the last completed run never reached (added since, or no run yet). */
  pendingCount: number;
  /** Products the last completed run covered. */
  lastRunCovered: number;
  /** "next sync in ~3h" — from `describeNextScheduledSync`; null when unknown. */
  nextSyncNote: string | null;
  priceMismatchCount: number;
  belowMapCount: number;
  suspiciousDiscountCount: number;
  stalePriceCount: number;
  staleCompareAtCount: number;
  missingLivePriceCount: number;
  unmappedVariantCount: number;
  /**
   * Where "review the unmapped variants" goes. The SKU Crosswalk review (`?review=shopify`) when the
   * store has `sku_crosswalk`; null falls back to the integration's Catalog Matching tab.
   */
  unmappedReviewHref?: string | null;
  rebateAmountCents: number;
  rebateUnits: number;
  rebateProgramCount: number;
  missingImages: number;
  catalogTotal: number;
  integrationId: string | null;
  /** Name of the platform the synced integration runs on ("BigCommerce"); defaults to Shopify. */
  platformLabel?: string;
  /** Products a Shopify sync held back because a listing might already be the same item. */
  listingHoldCount?: number;
  /** True when the count hit the list's cap, so it reads "50+". */
  listingHoldCountCapped?: boolean;
  /** Shopify integration with holds waiting (its Catalog Matching tab lists them). */
  listingHoldIntegrationId?: string | null;
  /** Whole days since the last successful sync, set only when that is past the stale threshold. */
  staleSyncDays?: number | null;
}

const formatCents = (cents: number) => `$${Math.round(cents / 100).toLocaleString()}`;

const countPhrase = (count: number, singular: string, plural = singular) =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

/** Short clause for the catalog-sync line. Distinct from the attention title. */
export function shopifyPriceGapClause(count: number): string {
  return count === 1
    ? `${count.toLocaleString()} Shopify price doesn't match Motzu`
    : `${count.toLocaleString()} Shopify prices don't match Motzu`;
}

/**
 * Reasons a Shopify listing was flagged, in the order a store should read them.
 * A listing can carry more than one reason, so these counts can add up to more
 * than the number of listings. Zero reasons are left out.
 */
export function describeShopifyPriceGaps(input: Pick<
  DeriveStoreAttentionInput,
  | 'belowMapCount'
  | 'suspiciousDiscountCount'
  | 'stalePriceCount'
  | 'staleCompareAtCount'
  | 'missingLivePriceCount'
  | 'unmappedVariantCount'
>): string {
  const parts = [
    input.belowMapCount > 0 ? countPhrase(input.belowMapCount, 'below MAP') : '',
    input.suspiciousDiscountCount > 0 ? countPhrase(input.suspiciousDiscountCount, 'priced far under MAP') : '',
    input.stalePriceCount > 0 ? countPhrase(input.stalePriceCount, 'still showing an old price') : '',
    input.staleCompareAtCount > 0
      ? countPhrase(input.staleCompareAtCount, 'still showing an old compare-at price', 'still showing old compare-at prices')
      : '',
    input.missingLivePriceCount > 0 ? countPhrase(input.missingLivePriceCount, 'with no price in Shopify') : '',
    input.unmappedVariantCount > 0
      ? countPhrase(input.unmappedVariantCount, 'not linked to a Motzu product', 'not linked to Motzu products')
      : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Pure derivation of the store dashboard's "Needs attention" list.
 * Rules (from design_handoff_store_admin_dashboard/README.md "Derived attention items"):
 * blocking before warning; within a severity, sort by dollars at risk desc then leave
 * insertion order (age isn't available for most of these signals); an item with zero
 * count is omitted entirely.
 */
export function deriveStoreAttention(input: DeriveStoreAttentionInput): AttentionItem[] {
  const items: AttentionItem[] = [];
  const integrationHref = input.integrationId ? `/app/integrations/${input.integrationId}` : '/app/integrations';
  const syncPlatform = input.platformLabel || 'Shopify';
  const hasIntegration = !!input.integrationId;

  if (input.blockedCount > 0) {
    items.push({
      id: 'blocked-sync',
      severity: 'blocking',
      title: `${countPhrase(input.blockedCount, 'product', 'products')} failed to reach your storefront`,
      meta: `${input.liveCount.toLocaleString()} of ${input.totalSelected.toLocaleString()} selected products confirmed live in ${syncPlatform}`,
      // Restarting a sync needs an integration to restart; without one, point at the list.
      action: hasIntegration ? 'Restart sync' : 'Open integrations',
      href: integrationHref,
    });
  }

  const listingHolds = input.listingHoldCount ?? 0;
  if (listingHolds > 0) {
    const count = `${listingHolds.toLocaleString()}${input.listingHoldCountCapped ? '+' : ''}`;
    items.push({
      id: 'listing-holds',
      severity: 'blocking',
      title: `${count} product${listingHolds !== 1 ? 's' : ''} waiting for a Shopify listing match`,
      meta: 'Sync will not create these until you confirm the existing Shopify listing or allow a new one',
      action: 'Confirm matches',
      href: input.listingHoldIntegrationId
        ? shopifyMappingHref(input.listingHoldIntegrationId, 'listing-matches')
        : '/app/integrations',
    });
  }

  if (input.pendingCount > 0) {
    const ran = input.lastRunCovered > 0
      ? `Only ${input.lastRunCovered.toLocaleString()} ${input.lastRunCovered === 1 ? 'was' : 'were'} in the last run`
      : 'No sync has run yet';
    items.push({
      id: 'pending-sync',
      severity: 'warning',
      title: `${input.pendingCount.toLocaleString()} selected product${input.pendingCount !== 1 ? 's' : ''} ${input.pendingCount !== 1 ? 'have' : 'has'} not synced yet`,
      meta: `${ran} · ${input.nextSyncNote || 'they push on the next scheduled sync'}`,
      action: 'Sync now',
      href: integrationHref,
    });
  }

  if (hasIntegration && input.staleSyncDays != null && input.staleSyncDays > 0) {
    items.push({
      id: 'stale-sync',
      severity: 'warning',
      title: `Last successful sync was ${countPhrase(input.staleSyncDays, 'day', 'days')} ago`,
      meta: `${syncPlatform} may be out of date with Motzu · ${input.nextSyncNote || 'check the sync log for what is stopping it'}`,
      action: 'Open sync log',
      href: integrationHref,
    });
  }

  if (input.priceMismatchCount > 0) {
    const auditHref = input.integrationId
      ? `/app/shopify-pricing-audit?integrationId=${input.integrationId}`
      : '/app/shopify-pricing-audit';
    items.push({
      id: 'price-mismatches',
      severity: 'blocking',
      title: countPhrase(
        input.priceMismatchCount,
        "Shopify listing doesn't match Motzu's price",
        "Shopify listings don't match Motzu's price"
      ),
      // Unmapped listings have their own item below; they are not a price problem.
      meta: describeShopifyPriceGaps({ ...input, unmappedVariantCount: 0 }) || "Shopify's price differs from the price Motzu would push",
      action: 'Review listings',
      href: auditHref,
    });
  }

  if (input.unmappedVariantCount > 0) {
    const count = input.unmappedVariantCount;
    const reviewHref = input.unmappedReviewHref
      || (input.integrationId ? `/app/integrations/${input.integrationId}?tab=matching` : '/app/integrations');
    items.push({
      id: 'unmapped-variants',
      severity: 'warning',
      title: countPhrase(
        count,
        'Shopify variant is not linked to a Motzu product',
        'Shopify variants are not linked to Motzu products'
      ),
      meta: input.unmappedReviewHref
        ? 'Their sales show as Unmatched and Motzu cannot price them · review suggested matches by SKU or UPC'
        : 'Their sales show as Unmatched and Motzu cannot price them · match them on the integration',
      action: 'Review matches',
      href: reviewHref,
    });
  }

  if (input.rebateAmountCents > 0) {
    items.push({
      id: 'rebates-identified',
      severity: 'warning',
      title: `${formatCents(input.rebateAmountCents)} in rebates identified from recent sales`,
      meta: `${input.rebateUnits.toLocaleString()} sold units across ${input.rebateProgramCount.toLocaleString()} program${input.rebateProgramCount !== 1 ? 's' : ''}`,
      action: 'Review rebates',
      href: '/app/sales-report',
    });
  }

  if (input.missingImages > 0) {
    const pct = input.catalogTotal > 0 ? Math.round((input.missingImages / input.catalogTotal) * 100) : 0;
    items.push({
      id: 'missing-images',
      severity: 'warning',
      title: `${input.missingImages.toLocaleString()} available products missing images`,
      meta: `${pct}% of catalog · products without imagery convert worse`,
      action: 'Open browser',
      href: '/app/products',
    });
  }

  const blocking = items.filter((i) => i.severity === 'blocking');
  const warning = items.filter((i) => i.severity === 'warning').sort((a, b) => {
    const dollarsA = a.id === 'rebates-identified' ? input.rebateAmountCents : 0;
    const dollarsB = b.id === 'rebates-identified' ? input.rebateAmountCents : 0;
    return dollarsB - dollarsA;
  });

  return [...blocking, ...warning];
}
